import React, { useState } from 'react';
import PatientTile from '../components/PatientTile';
import { mockPatients, Patient } from '../utils/mockData';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

// Mock data for practice analytics (move this to a separate file in a real application)
const treatmentEfficacyData = [
  { treatment: 'Vibration', efficacy: 75 },
  { treatment: 'Red Light', efficacy: 82 },
  { treatment: 'EMG', efficacy: 68 },
];

const deviceUsageData = [
  { month: 'Jan', usage: 120 },
  { month: 'Feb', usage: 150 },
  { month: 'Mar', usage: 180 },
  { month: 'Apr', usage: 200 },
  { month: 'May', usage: 220 },
];

const patientImprovementData = [
  { month: 'Jan', rate: 5 },
  { month: 'Feb', rate: 8 },
  { month: 'Mar', rate: 12 },
  { month: 'Apr', rate: 15 },
  { month: 'May', rate: 18 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const revenueGrowth = [
  { month: 'Jan', revenue: 5000, projectedTraditional: 4000 },
  { month: 'Feb', revenue: 6000, projectedTraditional: 4200 },
  { month: 'Mar', revenue: 7500, projectedTraditional: 4400 },
  { month: 'Apr', revenue: 8500, projectedTraditional: 4600 },
  { month: 'May', revenue: 10000, projectedTraditional: 4800 },
];

// Add this new mock data
const patientVolumeTrend = [
  { month: 'Jan', patients: 50 },
  { month: 'Feb', patients: 60 },
  { month: 'Mar', patients: 75 },
  { month: 'Apr', patients: 85 },
  { month: 'May', patients: 100 },
];

// Add this new mock data
const costSavings = {
  traditionalCosts: {
    officeRent: 2000,
    utilities: 500,
    staff: 3000,
    equipment: 1000,
  },
  myoworksCosts: {
    subscription: 500,
    equipmentLease: 300,
  },
  monthlySavings: 5700,
  annualSavings: 68400,
};

// Add this new mock data
const remotetreatmentSuccess = [
  { month: 'Jan', inPerson: 40, remote: 10 },
  { month: 'Feb', inPerson: 35, remote: 25 },
  { month: 'Mar', inPerson: 30, remote: 45 },
  { month: 'Apr', inPerson: 25, remote: 60 },
  { month: 'May', inPerson: 20, remote: 80 },
];

// Add this new mock data
const timeEfficiency = {
  averageSessionDuration: 25, // minutes
  patientsPerDay: 16,
  traditionalPatientsPerDay: 10,
  timesSaved: 6, // hours per week
};

// Add this new mock data for compliance alerts
const complianceAlerts = [
  { id: 1, patientName: 'John Doe', compliance: 75, lastActive: '2023-05-15' },
  { id: 2, patientName: 'Jane Smith', compliance: 68, lastActive: '2023-05-14' },
  { id: 3, patientName: 'Bob Johnson', compliance: 79, lastActive: '2023-05-16' },
];

const Dashboard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [injuryFilter, setInjuryFilter] = useState('');
  const [reportType, setReportType] = useState<'individual' | 'practice'>('practice');
  const [editableCostSavings, setEditableCostSavings] = useState(costSavings);
  const [editableTimeEfficiency, setEditableTimeEfficiency] = useState(timeEfficiency);

  const filteredPatients = mockPatients.filter(patient => 
    patient.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (injuryFilter === '' || patient.injury.toLowerCase() === injuryFilter.toLowerCase())
  );

  const uniqueInjuries = Array.from(new Set(mockPatients.map(p => p.injury)));

  const generateReport = () => {
    console.log(`Generating ${reportType} report...`);
    alert(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report generated!`);
  };

  const updateCostSavings = (category: 'traditionalCosts' | 'myoworksCosts', field: string, value: number) => {
    setEditableCostSavings(prevState => ({
      ...prevState,
      [category]: {
        ...prevState[category],
        [field]: value
      }
    }));
  };

  const updateTimeEfficiency = (field: keyof typeof timeEfficiency, value: number) => {
    setEditableTimeEfficiency(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  const totalTraditionalCosts = Object.values(editableCostSavings.traditionalCosts).reduce((a, b) => a + b, 0);
  const totalMyoworksCosts = Object.values(editableCostSavings.myoworksCosts).reduce((a, b) => a + b, 0);
  const monthlySavings = totalTraditionalCosts - totalMyoworksCosts;
  const annualSavings = monthlySavings * 12;
  const efficiencyIncrease = ((editableTimeEfficiency.patientsPerDay - editableTimeEfficiency.traditionalPatientsPerDay) / editableTimeEfficiency.traditionalPatientsPerDay * 100).toFixed(1);

  return (
    <div>
      <div className="flex justify-center mb-6">
        <img src="/myoworks-logo.png" alt="Myoworks Logo" className="h-32" />
      </div>

      {/* New Compliance Alerts Panel */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-2xl font-semibold mb-4">Compliance Alerts</h2>
        {complianceAlerts.length > 0 ? (
          <div className="space-y-4">
            {complianceAlerts.map(alert => (
              <div key={alert.id} className="flex items-center justify-between bg-red-100 p-4 rounded-lg">
                <div>
                  <p className="font-semibold">{alert.patientName}</p>
                  <p className="text-sm text-gray-600">Compliance: {alert.compliance}%</p>
                  <p className="text-sm text-gray-600">Last Active: {alert.lastActive}</p>
                </div>
                <button className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors">
                  Contact Patient
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p>No compliance alerts at this time.</p>
        )}
      </div>

      <h2 className="text-2xl font-semibold mb-6">Patient Dashboard</h2>
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <input
          type="text"
          placeholder="Search patients..."
          className="p-2 border rounded flex-grow"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="p-2 border rounded"
          value={injuryFilter}
          onChange={(e) => setInjuryFilter(e.target.value)}
        >
          <option value="">All Injuries</option>
          {uniqueInjuries.map(injury => (
            <option key={injury} value={injury}>{injury}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        {filteredPatients.map(patient => (
          <PatientTile key={patient.id} patient={patient} />
        ))}
      </div>

      {/* Practice Analytics Section */}
      <h2 className="text-2xl font-semibold mb-6">Practice Analytics</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Treatment Efficacy</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={treatmentEfficacyData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="efficacy"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {treatmentEfficacyData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Myoworks Device Usage</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={deviceUsageData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="usage" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Patient Improvement Rates</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={patientImprovementData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="rate" stroke="#8884d8" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Revenue Growth</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="revenue" stroke="#8884d8" fill="#8884d8" name="Actual Revenue" />
                <Area type="monotone" dataKey="projectedTraditional" stroke="#82ca9d" fill="#82ca9d" name="Projected Traditional" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Patient Volume Trends</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={patientVolumeTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="patients" stroke="#8884d8" name="Patient Count" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Cost Savings Calculator</h3>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">Traditional Costs</h4>
              {Object.entries(editableCostSavings.traditionalCosts).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between mb-2">
                  <label className="text-sm text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</label>
                  <input
                    type="number"
                    value={value}
                    onChange={(e) => updateCostSavings('traditionalCosts', key, Number(e.target.value))}
                    className="w-24 p-1 border rounded text-right"
                  />
                </div>
              ))}
            </div>
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">Myoworks Costs</h4>
              {Object.entries(editableCostSavings.myoworksCosts).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between mb-2">
                  <label className="text-sm text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</label>
                  <input
                    type="number"
                    value={value}
                    onChange={(e) => updateCostSavings('myoworksCosts', key, Number(e.target.value))}
                    className="w-24 p-1 border rounded text-right"
                  />
                </div>
              ))}
            </div>
            <div className="border-t pt-4">
              <p className="font-semibold text-green-600">Monthly Savings: ${monthlySavings.toFixed(2)}</p>
              <p className="font-semibold text-green-600">Annual Savings: ${annualSavings.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Remote Treatment Success</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={remotetreatmentSuccess}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="inPerson" stackId="a" fill="#8884d8" name="In-Person Treatments" />
                <Bar dataKey="remote" stackId="a" fill="#82ca9d" name="Remote Treatments" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Time Efficiency Metrics</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Session Duration (minutes):</span>
              <input
                type="number"
                value={editableTimeEfficiency.averageSessionDuration}
                onChange={(e) => updateTimeEfficiency('averageSessionDuration', Number(e.target.value))}
                className="w-20 p-1 border rounded text-right"
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Patients Per Day (Myoworks):</span>
              <input
                type="number"
                value={editableTimeEfficiency.patientsPerDay}
                onChange={(e) => updateTimeEfficiency('patientsPerDay', Number(e.target.value))}
                className="w-20 p-1 border rounded text-right"
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Patients Per Day (Traditional):</span>
              <input
                type="number"
                value={editableTimeEfficiency.traditionalPatientsPerDay}
                onChange={(e) => updateTimeEfficiency('traditionalPatientsPerDay', Number(e.target.value))}
                className="w-20 p-1 border rounded text-right"
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Time Saved Per Week (hours):</span>
              <input
                type="number"
                value={editableTimeEfficiency.timesSaved}
                onChange={(e) => updateTimeEfficiency('timesSaved', Number(e.target.value))}
                className="w-20 p-1 border rounded text-right"
              />
            </div>
            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Efficiency Increase:</span>
                <span className="font-semibold text-green-600">
                  {efficiencyIncrease}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Generate Reports</h3>
          <div className="space-y-4">
            <div>
              <label className="block mb-2">Report Type:</label>
              <select
                className="w-full p-2 border rounded"
                value={reportType}
                onChange={(e) => setReportType(e.target.value as 'individual' | 'practice')}
              >
                <option value="individual">Individual Patient</option>
                <option value="practice">Entire Practice</option>
              </select>
            </div>
            {reportType === 'individual' && (
              <div>
                <label className="block mb-2">Select Patient:</label>
                <select className="w-full p-2 border rounded">
                  {mockPatients.map(patient => (
                    <option key={patient.id} value={patient.id}>{patient.name}</option>
                  ))}
                </select>
              </div>
            )}
            <button
              className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              onClick={generateReport}
            >
              Generate Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;